<script lang="ts">
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { Http } from "@shared/svelte/Modules/ApiService";
  import { ProductFilteringEntity } from "@shared/services/event_tracking/entities";
  import FiltersWrapper from "@shared/svelte/Components/ProductLines/FiltersWrapper.svelte";
  import ProductLinesFilters from "@shared/svelte/Components/ProductLines/Filters.svelte";
  import getFilterParamsFromState from "@retailer/services/filter_params_from_state_params.js";
  import { cloneDeep, isEqual, omitBy } from "lodash";
  import { afterPageLoad, goto, params } from "@roxi/routify";
  import { updateProductLineListingStore } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";
  import { filterSelectedFiltersFromStateParams } from "@shared/services/filters";
  import SkeletonProductLineFilters from "@shared/svelte/Components/ProductLines/Skeletons/SkeletonProductLineFilters.svelte";

  const { trackClick } = EventTracking;

  export let mobileMode: boolean;
  export let overrideStateParams: any = null;
  export let overridePath = null;
  export let showIfNoResults = true;

  const filterAndPaginationPath = overridePath ? overridePath : "/product_listing";

  let availableFilters = null;

  // cloneDeep prevents the filters from changing data set at the
  // svelte page level that's responsible for determining if the page needs a full refresh
  let stateParams = cloneDeep(overrideStateParams || $params) || {};
  let initialStateParams = cloneDeep(stateParams);

  const closeFilters = () => {
    stateParams = cloneDeep(initialStateParams);

    updateProductLineListingStore(n => {
      n.mobileFiltersVisible = false;
      return n;
    });

    loadAvailableFilters();
  };

  const cleanFilterParams = filterParams => {
    return omitBy(filterParams, function (x) {
      return x === null || x === undefined || x === "null" || x === "undefined" || x === "";
    });
  };

  let selectedFiltersCount: number;
  const loadAvailableFilters = () => {
    Http(
      {
        method: "GET",
        url: "/api/retailer/product_lines/available_filters",
        data: cleanFilterParams(getFilterParamsFromState(stateParams)),
      },
      (json, _) => {
        availableFilters = json;
        if (availableFilters?.selected_filters) {
          availableFilters.selected_filters =
            filterSelectedFiltersFromStateParams(stateParams, json) || [];
          selectedFiltersCount = availableFilters.selected_filters.length;
          updateProductLineListingStore(n => {
            n.filtersCount = selectedFiltersCount;
            return n;
          });
        }
      },
      null,
    );
  };

  const onSearch = changes => {
    changes = cleanFilterParams(changes);
    stateParams = changes;
    loadAvailableFilters();
  };

  const refreshFilters = changes => {
    changes = cleanFilterParams(changes);
    stateParams = changes;

    if (mobileMode) {
      loadAvailableFilters();
    } else {
      applyFilters();
    }
  };

  const applyFilters = () => {
    initialStateParams = cloneDeep(stateParams);
    trackClick(null, new ProductFilteringEntity(stateParams));

    $goto(filterAndPaginationPath, stateParams);
  };

  let disableApplyButton: boolean;
  let disableClearButton: boolean;
  $: {
    disableClearButton = !selectedFiltersCount;
    disableApplyButton = isEqual(initialStateParams, stateParams);
  }

  const setup = () => {
    loadAvailableFilters();
    return true;
  };

  // This needs to be run both times, the first so filters load, and the second so they refresh correctly when filters are applied/unapplied.
  setup();
  $afterPageLoad(page => {
    stateParams = cloneDeep(overrideStateParams || $params) || {}; // Without this, clicking back behaves weird.
    return setup();
  });
</script>

{#key availableFilters}
  <FiltersWrapper bind:mobileMode>
    {#if availableFilters && availableFilters.selected_filters !== undefined}
      <ProductLinesFilters
        {onSearch}
        {showIfNoResults}
        autoExpandInDesktop={true}
        disableFiltersDuringProcessing={true}
        filters={availableFilters}
        onChange={refreshFilters}
        onClose={closeFilters}
        onApply={applyFilters}
        bind:disableApplyButton
        bind:disableClearButton
        bind:mobileMode
        bind:stateParams
      />
    {:else}
      <SkeletonProductLineFilters />
    {/if}
  </FiltersWrapper>
{/key}
